import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import isObject from 'lodash/isObject';

import {
  selectCountryList,
  selectCountryPhoneSettings,
} from '../../redux/data/selectors';
import {
  selectPatientPhoneNumberCountryIsoCode,
  selectUserPhoneNumberCountryCode,
  selectUserPhoneNumberCountryISOCode,
} from '../../redux/profiles/selectors';
import { selectDependentPhoneNumberCountryCode } from '../../redux/dependentForm/selectors';
import { actions as currentCountryActions } from '../../redux/profiles/country-code';

const DEFAULT_CODE = '+1';

export function useMobileInput(phoneValue, formatMessage, phoneCode) {
  const userPhoneNumberCountryCode = useSelector(
    selectUserPhoneNumberCountryCode,
  );
  const userPhoneNumberCountryISOCode = useSelector(
    selectUserPhoneNumberCountryISOCode,
  );
  const dependentPhoneNumberCountryCode = useSelector(
    selectDependentPhoneNumberCountryCode,
  );
  const patientPhoneNumberCountryIsoCode = useSelector(
    selectPatientPhoneNumberCountryIsoCode,
  );
  const countryList = useSelector(selectCountryList);

  const adaptInitialValueCountryCodeByIsoCode = isoCode => {
    const target = countryList.find(item => {
      return get(item, 'isoCode', '') === isoCode;
    });
    return target ? target.phone.countryCode : '';
  };

  const handleSetInitialValuePhoneNumber = (value, code) => {
    return value
      ? value.replace(
          code ||
            adaptInitialValueCountryCodeByIsoCode(
              patientPhoneNumberCountryIsoCode,
            ) ||
            dependentPhoneNumberCountryCode ||
            userPhoneNumberCountryCode ||
            DEFAULT_CODE,
          '',
        )
      : '';
  };
  const dispatch = useDispatch();
  const countryPhoneSettings = useSelector(selectCountryPhoneSettings);

  const [code, setCode] = useState(
    phoneCode ||
      adaptInitialValueCountryCodeByIsoCode(patientPhoneNumberCountryIsoCode) ||
      dependentPhoneNumberCountryCode ||
      userPhoneNumberCountryCode ||
      DEFAULT_CODE,
  );
  const [phone, setPhone] = useState(
    handleSetInitialValuePhoneNumber(phoneValue, phoneCode) || '',
  );

  useEffect(() => {
    if (phoneValue && !phone) {
      setPhone(handleSetInitialValuePhoneNumber(phoneValue, phoneCode));
    }
  }, [phoneValue]);

  useEffect(() => {
    if (dependentPhoneNumberCountryCode && phoneValue) {
      setCode(dependentPhoneNumberCountryCode);
      dispatch(
        currentCountryActions.set(
          handleSetCountryIsoCode(dependentPhoneNumberCountryCode),
        ),
      );
    }
  }, [dependentPhoneNumberCountryCode]);

  const handleSetCountryIsoCode = useMemo(
    () => data => {
      if (isObject(data)) return data.isoCode;
      const target = countryList.find(item => {
        if (patientPhoneNumberCountryIsoCode) {
          return get(item, 'isoCode', '') === patientPhoneNumberCountryIsoCode;
        }
        return (
          get(item, 'phone.countryCode', '') === data &&
          (get(item, 'isoCode', '') === dependentPhoneNumberCountryCode ||
            get(item, 'isoCode', '') === userPhoneNumberCountryISOCode)
        );
      });
      return target ? target.isoCode : '';
    },
    [code, countryList],
  );

  const codeOptions = useMemo(
    () =>
      countryList.map(({ isoCode, name, phone: { countryCode } }) => ({
        label: `${countryCode} ${name}`,
        value: { isoCode, countryCode },
      })),
    [countryList],
  );

  const handleCodeChange = useCallback(({ value }) => {
    setCode(value.countryCode);
  }, []);

  const limit = useMemo(() => {
    const match = countryPhoneSettings.find(
      ({ countryCode }) => countryCode === code,
    );

    return match ? match.numberLength : 8;
  }, [code, countryPhoneSettings]);

  const handlePhoneChange = useCallback(value => {
    setPhone(value);
  }, []);

  const validate = useCallback(
    value => {
      let errors = null;
      if (value.length === 0) return null;
      if (value.length > limit)
        errors = [
          formatMessage({ id: 'profile.fields.mobilePhone.validation.long' }),
        ];
      if (value.length < limit)
        errors = [
          formatMessage({ id: 'profile.fields.mobilePhone.validation.short' }),
        ];

      return errors;
    },
    [limit],
  );

  return {
    codeControl: {
      options: codeOptions,
      handleSetCountryIsoCode,
      handleCodeChange,
      code,
    },
    phoneControl: {
      handlePhoneChange,
      phone,
    },
    validation: {
      validate,
      limit,
    },
  };
}



// WEBPACK FOOTER //
// ./src/components/MobilePhone/useMobileInput.js